import { Component, inject, OnDestroy, OnInit, output, signal, TemplateRef, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SwalComponent, SwalPortalTargets } from '@sweetalert2/ngx-sweetalert2';
import { parseInt as parseInteger } from 'lodash';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { finalize, map } from 'rxjs/operators';
import { hasStatus, tryGetErrorMessage } from '../../../../error.utils';
import { SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS } from '../../../../helpers';
import { AssignmentService } from '../../../../service/assignment.service';
import { DebugService } from '../../../../service/debug.service';
import { LocationService } from '../../../../service/location.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../service/notify.service';
import {
  AssignmentDetails,
  IncidentFormContext,
  IncidentFormGroup,
  LocationResponse,
  ReviewStatus,
  ReviewSubjectType,
  SubmitReviewRequest,
} from '../../../../types';
import { ReviewSubmissionFailedModalComponent } from '../review-submission-failed-modal/review-submission-failed-modal.component';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { parseReviewSubjectType, reviewOutcomeToFormValues } from '../review-utils';
import { AssignmentDetailsStore } from '../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-self-service-assignment',
  templateUrl: './self-service-assignment.component.html',
  styleUrl: './self-service-assignment.component.scss',
  standalone: false,
  providers: [AssignmentDetailsStore],
})
export class SelfServiceAssignmentComponent implements OnInit, OnDestroy {
  public reviewFormService = inject(ReviewFormService);
  public swalTargets = inject(SwalPortalTargets);
  public debug = inject(DebugService);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private assignmentDetailsStore = inject(AssignmentDetailsStore);
  private notifyService = inject(NotifyService);
  private locationService = inject(LocationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  public assignmentId = signal<string | undefined>(undefined);
  public incidentType = signal<ReviewSubjectType | undefined>(undefined);
  public readonly ReviewSubjectType = ReviewSubjectType;
  public assignment = signal<AssignmentDetails | undefined>(undefined);

  public locationId = signal<number | undefined>(undefined);
  public instanceAtDate = signal<Date | undefined>(undefined);
  public imageId = signal<string | null | undefined>(undefined);
  public location = signal<LocationResponse | undefined>(undefined);
  public form = signal<IncidentFormGroup | undefined>(undefined);
  public formTemplate: TemplateRef<unknown> | null = null;

  public isLoading = signal<boolean>(false);
  public isCompleted = signal<boolean>(false);
  public isSubmitting = signal<boolean>(false);
  public errorMessage = signal<string | undefined>(undefined);
  public submitFailureError = signal<string | undefined>(undefined);
  private submittedSwal = viewChild<SwalComponent>('submittedSwal');
  private failedModal = viewChild<ReviewSubmissionFailedModalComponent>('failedModal');

  public isLoadingAvailableAssignments = signal<boolean>(false);
  public hasAvailableAssignments = signal<boolean>(false);
  public nextReviewError = signal<string | undefined>(undefined);
  public preferredLocationId = signal<number | undefined>(undefined);

  public debugNextAssignmentLink = signal<string | undefined>(undefined);
  private locationQueryParam = injectQueryParams(SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS.LOCATION_ID);

  // outputs
  public submitSuccess = output<{ locationId?: number }>();

  public ngOnInit(): void {
    this.assignmentId.set(this.route.snapshot.params.assignmentId);
    this.loadAssignment();

    const locationId = parseInteger(this.locationQueryParam(), 10);
    this.preferredLocationId.set(Number.isNaN(locationId) ? undefined : locationId);

    const incidentType = this.route.snapshot.data.incidentType;
    if (incidentType) this.incidentType.set(parseReviewSubjectType(String(incidentType)));
  }

  public ngOnDestroy() {
    this.reviewFormService.resetForm();
  }

  public onContextReady(ctx: IncidentFormContext) {
    this.locationId.set(ctx.locationId);
    this.instanceAtDate.set(ctx.instanceAtDate);
    this.form.set(ctx.form);

    const locId = ctx.locationId;
    if (locId) {
      this.locationService.getLocation({ locationId: locId }).subscribe(
        (location) => this.location.set(location),
        () => {},
      );
    }
  }

  public submit() {
    const review = this.form().value;
    const findings = this.reviewFormService.findingsForm.value;
    const locationId = this.locationId();
    const branchId = this.location()?.branch_id;

    if (!locationId || !branchId) {
      this.notifyService.notify('Missing location or branch information');
      return;
    }

    this.isSubmitting.set(true);

    this.reviewFormService.uploadFiles().subscribe(
      ({ uploadedPhotosLinks, uploadedReportsLinks }) => {
        const submitRequest: SubmitReviewRequest = {
          is_theft: review.isTheft,
          case_id: review.matchingCase === 'new_case' ? null : review.matchingCase,
          report_links: uploadedReportsLinks,
          photos_links: uploadedPhotosLinks,
          extra_links: review.extraLinks.map((l) => ({ link: l })),
          theft_type: {
            is_cancellation: review.theftType.is_cancellation ?? false,
            is_underringing: review.theftType.is_underringing ?? false,
            is_walkout: review.theftType.is_walkout ?? false,
            is_cashier: review.theftType.is_cashier ?? false,
          },
          suspected_items: review.suspectedItems?.map((i) => ({ id: i.id!, total: i.total })),
          comments: review.isTheft ? findings.comments : this.reviewFormService.getNtfReason(),
          additional_stolen_value: review.additionalStolenValue,
          instance_at: this.reviewFormService.getValidInstanceAt(review.instance_at, this.instanceAtDate()),
          recent_walk_in_ids: review.selectedImages,
          use_kiosk_photo: review.useKioskPhoto,
        };

        this.lpService
          // submitSelfServiceReview is actually only for incident submission (forgot)
          .submitReview(this.assignmentId(), submitRequest)
          .pipe(finalize(() => this.isSubmitting.set(false)))
          .subscribe(
            () => {
              this.submitSuccess.emit({ locationId });
              this.checkAvailableAssignments();
              this.submittedSwal()?.fire();
            },
            (err) => {
              this.submitFailureError.set(tryGetErrorMessage(err));
              this.failedModal()?.open();
            },
          );
      },
      (err) => {
        this.isSubmitting.set(false);
        this.notifyService.notify(`Failed to upload images: ${err?.message ?? err}`);
      },
    );
  }

  public redirectToNextAssignment() {
    if (!this.hasAvailableAssignments()) return;
    this.getNextAssignmentLink().subscribe(
      (link) => (window.location.href = link),
      (err) => this.nextReviewError.set(err?.error?.error ?? 'Failed to fetch next review'),
    );
  }

  public checkAvailableAssignments() {
    this.isLoadingAvailableAssignments.set(true);
    this.hasAvailableAssignments.set(false);
    this.lpService
      .hasAnyAvailableAssignments(this.preferredLocationId())
      .pipe(finalize(() => this.isLoadingAvailableAssignments.set(false)))
      .subscribe(
        (data) => this.hasAvailableAssignments.set(!!data?.has_any_reviews),
        (err) => this.nextReviewError.set(err?.error?.error ?? 'Failed to check assignments'),
      );
  }

  public getNextAssignmentLink() {
    return this.assignmentService.getNextAssignment(this.preferredLocationId()).pipe(
      map((assignment) => {
        const reviewId = assignment.assignment_id;
        const urlTree = this.router.createUrlTree([`/lp/self-service/${reviewId}`], { queryParamsHandling: 'preserve' });
        return this.router.serializeUrl(urlTree);
      }),
    );
  }

  public loadDebugNextAssignmentLink() {
    this.debugNextAssignmentLink.set(undefined);
    this.getNextAssignmentLink().subscribe(
      (link) => this.debugNextAssignmentLink.set(link),
      (err) => this.nextReviewError.set(err?.error?.error ?? 'Failed to fetch next review'),
    );
  }

  public back() {
    this.router.navigate(['/lp/self-service']);
  }

  private loadAssignment() {
    this.assignmentService
      .getAssignment(this.assignmentId())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (assignment) => {
          this.assignment.set(assignment);
          this.assignmentDetailsStore.set(assignment);
          this.incidentType.set(parseReviewSubjectType(assignment.subject_type));
          // load existing submission so users can VIEW (not edit)
          if (assignment.status.toLowerCase() === ReviewStatus.Completed || assignment.status.toLowerCase() === ReviewStatus.Validated) {
            this.isCompleted.set(true);
            this.isLoading.set(true);
            this.assignmentService
              .getReviewOutcomes(this.assignmentId())
              .pipe(finalize(() => this.isLoading.set(false)))
              .subscribe((outcomes) => {
                if (!outcomes || outcomes.length === 0) return;
                const last = outcomes[outcomes.length - 1];
                const v = reviewOutcomeToFormValues(last);
                this.reviewFormService.applyInitialValues(v);
              });
          }
        },
        (err) => {
          if (hasStatus(err) && err.status === 404) {
            this.errorMessage.set('Assignment was not found (it may have been archived)');
          }
        },
      );
  }
}
